import {gql, mutate, watchQuery} from '@/services/apollo'
import {getToken} from '@/keycloak'

const FRAGMENTS = {
  INTEGRATION: gql`
    fragment Integration on Integration {
      id,
      name,
      integrationId,
      bridgeId,
      gameRoundValidityPeriod
      defaultCasinoId
      properties {
        key
        value
      }
    }
  `,

  INTEGRATION_CASINOS: gql`
    fragment IntegrationCasinos on Integration {
      id,
      name,
      integrationId,
      bridgeId,
      gameRoundValidityPeriod
      defaultCasinoId
      properties {
        key
        value
      }
      casinos {
        casinoId
        name
        maxRTP
        options{
          showDevelopStartPage
          sendEventMessages
        }
      }
      bridge {
        name
        routerUrl
      }
    }
  `,
  INTEGRATION_GAME: gql`
    fragment IntegrationGame on IntegrationGame {
      id
      active
      integrationId
      integration {
        id
        name
      }
      gameId
      game {
        id
        name
      }
      gameVersionId
      gameVersion {
        id
        version
      }
    }
  `
}

const GET_GAMES = gql`
  query GetGames($input: IntegrationGamesInput!) {
    integrationGames(input: $input) {
      id
      active
      gameVersion {
        id
        gameId
        version
        frontendUrl
        backendUrl
      }
      game {
        id
        name
      }
    }
  }
`

const GET_INTEGRATION_GAMES = gql`
  query IntegrationGames($input: IntegrationGamesInput!) {
    integrationGames(input: $input) {
      id
      active
      integration {
        name
      }
      gameVersion {
        id
        version
      }
    }
  }
`

const GET_INTEGRATION = gql`
  query IntegrationQuery($input: IntegrationInput!) {
    integration(input: $input) {
      ...IntegrationCasinos
    }
  }

  ${FRAGMENTS.INTEGRATION_CASINOS}
`

const GET_INTEGRATION_GAME = gql`
  query IntegrationGame($input: IntegrationGameInput!) {
    integrationGame(input: $input) {
      ...IntegrationGame
    }
  }

  ${FRAGMENTS.INTEGRATION_GAME}
`

const GET_INTEGRATIONS = gql`
  query IntegrationPage($input: IntegrationPageInput!) {
    integrationPage(input: $input) {
      integration {
        id
        name
        defaultCasinoId
      }
      totalCount(input: $input)
    }
  }
`

const CREATE_INTEGRATION = gql`
  mutation CreateIntegration($input: CreateIntegrationInput!) {
    createIntegration(input: $input) {
      ...Integration
    }
  }

  ${FRAGMENTS.INTEGRATION}
`

const EDIT_INTEGRATION = gql`
  mutation EditIntegration($input: EditIntegrationInput!) {
    editIntegration(input: $input) {
      ...Integration
    }
  }

  ${FRAGMENTS.INTEGRATION}
`

const DELETE_INTEGRATION = gql`
  mutation DeleteIntegration($input: DeleteIntegrationInput!) {
    deleteIntegration(input: $input)
  }
`

const CREATE_INTEGRATION_GAME = gql`
  mutation CreateIntegrationGame($input: CreateIntegrationGameInput!) {
    createIntegrationGame(input: $input) {
      id,
      active,
      integrationId,
      gameId,
      gameVersionId
    }
  }
`

const DELETE_INTEGRATION_GAME = gql`
  mutation DeleteIntegrationGame($input: DeleteIntegrationGameInput!) {
    deleteIntegrationGame(input: $input)
  }
`

const DEACTIVATE_INTEGRATION_GAME = gql`
  mutation DeactivateIntegrationGame($input: DeactivateIntegrationGameInput!) {
    deactivateIntegrationGame(input: $input) {
      id,
      active,
      integrationId,
      gameId,
      gameVersionId
    }
  }
`

const ACTIVATE_INTEGRATION_GAME = gql`
  mutation activateIntegrationGame($input: ActivateIntegrationGameInput!) {
    activateIntegrationGame(input: $input) {
      id,
      active,
      integrationId,
      gameId,
      gameVersionId
    }
  }
`

const GET_GAME_VERSIONS_BY_GAME = gql`
  query GameVersionPage($input: GameVersionPageInput!) {
    gameVersionPage(input: $input) {
      gameVersionsByGame {
        game {
          id
          name
        }
        gameVersions {
          id
          gameId
          version
          frontendUrl
          backendUrl
        }
      }
      totalCount
    }
  }
`
export default {
  createIntegrationGame (input) {
    return mutate({
      mutation: CREATE_INTEGRATION_GAME,
      variables: { input },
      refetchQueries: () => {
        return ['GetGames', 'GameVersionPage']
      }
    })
  },
  deleteIntegrationGame (input) {
    return mutate({
      mutation: DELETE_INTEGRATION_GAME,
      variables: { input },
      refetchQueries: () => {
        return ['GetGames', 'GameVersionPage']
      }
    })
  },
  deactivateIntegrationGame (input) {
    return mutate({
      mutation: DEACTIVATE_INTEGRATION_GAME,
      variables: { input }
    })
  },
  activateIntegrationGame (input) {
    return mutate({
      mutation: ACTIVATE_INTEGRATION_GAME,
      variables: { input }
    })
  },
  getIntegrations () {
    return watchQuery({ query: GET_INTEGRATIONS, variables: { input: { limit: 0, offset: 0 } } })
  },
  getIntegrationGames (variables) {
    return watchQuery({
      query: GET_INTEGRATION_GAMES,
      variables
    })
  },
  getGames (variables) {
    return watchQuery({
      query: GET_GAMES,
      variables
    })
  },
  getIntegration (variables) {
    return watchQuery({ query: GET_INTEGRATION, variables })
  },
  getIntegrationGame (variables) {
    return watchQuery({
      query: GET_INTEGRATION_GAME,
      variables
    })
  },
  getGameVersionsByGame (variables) {
    return watchQuery({ query: GET_GAME_VERSIONS_BY_GAME, variables })
  },
  createIntegration (input) {
    delete input["casinos"]
    return mutate({
      mutation: CREATE_INTEGRATION,
      variables: { input }
    })
  },
  editIntegration (input) {
    delete input["casinos"]
    input.properties = input.properties || []
    return mutate({
      mutation: EDIT_INTEGRATION,
      variables: { input }
    })
  },
  deleteIntegration (input) {
    return mutate({
      mutation: DELETE_INTEGRATION,
      variables: { input }
    })
  },
  async getIntegrationConfiguration (integrationId) {
    const response = await fetch('/integration/configuration/validate', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + await getToken()
      },
      body: JSON.stringify({
        integrationId: integrationId,
      })
    })

    if (response.status !== 200)
      throw response.status
    return await response.json()
  },
  async pushIntegrationConfiguration (integrationId) {
    const response = await fetch('/integration/configuration/push', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + await getToken()
      },
      body: JSON.stringify({
        integrationId: integrationId,
      })
    })

    if (response.status !== 200) {
      throw await response.text()
    }

    return response
  }
}
